import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Typography, notification, Spin, Tag, Tabs, Popconfirm, Input, Rate } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, WarningFilled } from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
// import MainLayout from '../layouts/MainLayout'; // Entfernt
import api from '../services/api'; // Importiere das api-Objekt
// Importiere Review-Service-Funktionen
import { getPendingReviews, approveReview, deleteReview } from '../services/reviewService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input; // Für die Notizen

// Helper function to format date
const formatDate = (dateString) => {
    try {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'Ungültiges Datum' : date.toLocaleDateString('de-DE', options);
    } catch (e) {
        return dateString || 'Unbekanntes Datum';
    }
};

// Funktion für Ampel-Farben
const getReportCountTagColor = (count) => {
    if (count >= 4) return 'red';
    if (count >= 2) return 'orange';
    return 'default'; // Oder null für Standard-Grau
};

const AdminManagementPage = () => {
    const [activeTab, setActiveTab] = useState('pendingReviews'); // State für aktiven Tab
    const [reportedShops, setReportedShops] = useState([]);
    const [inaccuracyReports, setInaccuracyReports] = useState([]); // NEU: State für Ungenauigkeitsmeldungen
    const [pendingReviews, setPendingReviews] = useState([]); // NEU: State für ausstehende Reviews
    const [loadingReported, setLoadingReported] = useState(true);
    const [loadingInaccuracy, setLoadingInaccuracy] = useState(false); // NEU: Loading-State für Ungenauigkeitsmeldungen
    const [loadingPendingReviews, setLoadingPendingReviews] = useState(true); // NEU: Loading-State für Reviews
    const [actionLoading, setActionLoading] = useState({});
    const [resolutionNotes, setResolutionNotes] = useState({}); // State für Admin-Notizen pro Report

    // --- Datenabruf ---

    const fetchReportedShops = useCallback(async () => {
        setLoadingReported(true);
        try {
            const response = await api.get('/admin/reported-shops'); // Angepasste Route
            if (response.success) {
                setReportedShops(response.data || []);
            } else {
                 throw new Error(response.message || 'Fehler beim Laden der gemeldeten Shops');
            }
        } catch (error) {
            console.error("Fehler beim Laden der gemeldeten Shops:", error);
            notification.error({
                message: 'Fehler beim Laden (Gemeldete Shops)',
                description: error.message || 'Konnte gemeldete Shops nicht abrufen.'
            });
            setReportedShops([]);
        } finally {
            setLoadingReported(false);
        }
    }, []);

    // NEU: Funktion zum Abrufen der Ungenauigkeitsmeldungen
    const fetchInaccuracyReports = useCallback(async () => {
        setLoadingInaccuracy(true);
        try {
            const response = await api.get('/admin/inaccuracy-reports');
            if (response.success) {
                // Die Daten sind jetzt die aggregierte Liste
                setInaccuracyReports(response.data || []);
            } else {
                 throw new Error(response.message || 'Fehler beim Laden der Ungenauigkeitsmeldungen');
            }
        } catch (error) {
            console.error("Fehler beim Laden der Ungenauigkeitsmeldungen:", error);
            notification.error({
                message: 'Fehler beim Laden (Ungenauigkeiten)',
                description: error.message || 'Konnte Ungenauigkeitsmeldungen nicht abrufen.'
            });
            setInaccuracyReports([]);
        } finally {
            setLoadingInaccuracy(false);
        }
    }, []);

    // NEU: Funktion zum Abrufen der ausstehenden Bewertungen
    const fetchPendingReviews = useCallback(async () => {
        setLoadingPendingReviews(true);
        try {
            const response = await getPendingReviews(); // Nutze die Service-Funktion
            if (response.success) {
                setPendingReviews(response.data || []);
            } else {
                 throw new Error(response.message || 'Fehler beim Laden der ausstehenden Bewertungen');
            }
        } catch (error) {
            console.error("Fehler beim Laden der ausstehenden Bewertungen:", error);
            notification.error({
                message: 'Fehler beim Laden (Bewertungen)',
                description: error.message || 'Konnte ausstehende Bewertungen nicht abrufen.'
            });
            setPendingReviews([]);
        } finally {
            setLoadingPendingReviews(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'reported') {
            fetchReportedShops();
        } else if (activeTab === 'inaccuracy') {
            fetchInaccuracyReports();
        } else if (activeTab === 'pendingReviews') { // NEU: Bedingung für Reviews
            fetchPendingReviews();
        }
    }, [activeTab, fetchReportedShops, fetchInaccuracyReports, fetchPendingReviews]);

    // --- Handler für Aktionen ---

    const handleReportedShopAction = async (shopId, action) => {
        setActionLoading(prev => ({ ...prev, [`reported-${shopId}`]: true }));
        // Verwende die korrekten Endpunkte aus adminRoutes
        const endpoint = `/admin/reported-shops/${shopId}/${action}`; // 'approve' or 'reject'
        const successMessage = action === 'approve' ? 'Shop genehmigt!' : 'Shop abgelehnt.';
        const errorMessage = action === 'approve' ? 'Fehler beim Genehmigen' : 'Fehler beim Ablehnen';
        let response;

        try {
            if (action === 'approve' || action === 'reject') {
                response = await api.put(endpoint);
            } else {
                 // Optional: Löschen hinzufügen
                 // endpoint = `/admin/reported-shops/${shopId}`;
                 // response = await api.delete(endpoint);
                 // successMessage = 'Shop gelöscht.';
                 // errorMessage = 'Fehler beim Löschen';
                 console.warn("Löschfunktion noch nicht implementiert"); // Platzhalter
                 return; // Frühzeitig beenden, da nicht implementiert
            }

             if (!response || !response.success) {
                 throw new Error(response?.message || 'Aktion fehlgeschlagen.');
            }
            notification.success({ message: successMessage });
            // Update state
            setReportedShops(prevShops => prevShops.filter(shop => shop._id !== shopId));

        } catch (error) {
            console.error(`${errorMessage}:`, error);
            notification.error({
                message: errorMessage,
                description: error.message || 'Aktion fehlgeschlagen.'
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`reported-${shopId}`]: false }));
        }
    };

    // NEU: Handler für Aktionen bei Ungenauigkeitsmeldungen
    const handleInaccuracyAction = async (reportId, action) => {
        setActionLoading(prev => ({ ...prev, [`inaccuracy-${reportId}`]: true }));
        const notes = resolutionNotes[reportId] || ''; // Notizen aus dem State holen
        const body = notes ? { resolutionNotes: notes } : {};
        const endpoint = `/admin/inaccuracy-reports/${reportId}/${action}`; // 'resolve' or 'reject'
        const successMessage = action === 'resolve' ? 'Meldung(en) behoben & Shop deaktiviert!' : 'Meldung(en) abgelehnt.';
        const errorMessage = action === 'resolve' ? 'Fehler beim Beheben' : 'Fehler beim Ablehnen';

        try {
            const response = await api.put(endpoint, body); // PUT Request mit optionalen Notizen
             if (!response.success) {
                 throw new Error(response.message || 'Aktion fehlgeschlagen.');
            }
            notification.success({ message: successMessage });
            // Update state - remove the processed aggregated report
            setInaccuracyReports(prevReports => prevReports.filter(report => report._id !== reportId));
            // Notizen für diesen Report entfernen
            setResolutionNotes(prev => {
                const newNotes = {...prev};
                delete newNotes[reportId];
                return newNotes;
            });
        } catch (error) {
            console.error(`${errorMessage}:`, error);
            notification.error({
                message: errorMessage,
                description: error.message || 'Aktion fehlgeschlagen.'
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`inaccuracy-${reportId}`]: false }));
        }
    };

    // NEU: Handler zum Ändern der Notizen im State
    const handleNoteChange = (reportId, value) => {
        setResolutionNotes(prev => ({ ...prev, [reportId]: value }));
    };

    // NEU: Handler zum Genehmigen einer Bewertung
    const handleApproveReview = async (reviewId) => {
        setActionLoading(prev => ({ ...prev, [`review-approve-${reviewId}`]: true }));
        try {
            const response = await approveReview(reviewId); // Service-Funktion nutzen
            if (response.success) {
                notification.success({ message: 'Bewertung erfolgreich genehmigt!' });
                // Bewertung aus der Liste entfernen
                setPendingReviews(prevReviews => prevReviews.filter(review => review._id !== reviewId));
            } else {
                throw new Error(response.message || 'Fehler beim Genehmigen der Bewertung.');
            }
        } catch (error) {
            console.error('Fehler beim Genehmigen der Bewertung:', error);
            notification.error({
                message: 'Fehler beim Genehmigen',
                description: error.message || 'Die Bewertung konnte nicht genehmigt werden.'
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`review-approve-${reviewId}`]: false }));
        }
    };

    // NEU: Handler zum Löschen einer (ausstehenden) Bewertung
    const handleDeleteReview = async (reviewId) => {
        setActionLoading(prev => ({ ...prev, [`review-delete-${reviewId}`]: true }));
        try {
            const response = await deleteReview(reviewId); // Service-Funktion nutzen
            if (response.success) {
                notification.success({ message: 'Bewertung erfolgreich gelöscht!' });
                // Bewertung aus der Liste entfernen
                setPendingReviews(prevReviews => prevReviews.filter(review => review._id !== reviewId));
            } else {
                throw new Error(response.message || 'Fehler beim Löschen der Bewertung.');
            }
        } catch (error) {
            console.error('Fehler beim Löschen der Bewertung:', error);
            notification.error({
                message: 'Fehler beim Löschen',
                description: error.message || 'Die Bewertung konnte nicht gelöscht werden.'
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`review-delete-${reviewId}`]: false }));
        }
    };

    // --- Tabellen-Spalten ---

    const reportedColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
        { title: 'Adresse', key: 'address', render: (_, record) => `${record.address || ''}, ${record.postalCode || ''} ${record.city || ''}`, sorter: (a, b) => `${a.address}, ${a.postalCode} ${a.city}`.localeCompare(`${b.address}, ${b.postalCode} ${b.city}`) },
        { title: 'Gemeldet', dataIndex: 'reportCount', key: 'reportCount', align: 'center', sorter: (a, b) => a.reportCount - b.reportCount, render: (count) => <Tag color={count > 1 ? 'blue' : undefined}>{count || 0}x</Tag> },
        { title: 'Gemeldet am', dataIndex: 'createdAt', key: 'createdAt', render: (date) => date ? new Date(date).toLocaleDateString('de-DE') : '-', sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
        {
            title: 'Aktionen',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleReportedShopAction(record._id, 'approve')}
                        loading={actionLoading[`reported-${record._id}`]}
                        disabled={actionLoading[`reported-${record._id}`]}
                    >
                        Genehmigen
                    </Button>
                    <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleReportedShopAction(record._id, 'reject')}
                        loading={actionLoading[`reported-${record._id}`]}
                        disabled={actionLoading[`reported-${record._id}`]}
                    >
                        Ablehnen
                    </Button>
                    {/* Optional: Delete Button */}
                    {/* <Popconfirm title="Sicher löschen?" onConfirm={() => handleReportedShopAction(record._id, 'delete')} okText="Ja" cancelText="Nein">
                        <Button danger icon={<DeleteOutlined />} loading={actionLoading[`reported-${record._id}`]} disabled={actionLoading[`reported-${record._id}`]} />
                    </Popconfirm> */}
                </Space>
            ),
        },
    ];

    // NEU: Angepasste Spalten für Ungenauigkeitsmeldungen
    const inaccuracyColumns = [
        {
            title: 'Shop',
            key: 'shop',
            render: (_, record) => record.shop ? `${record.shop.name} (${record.shop.city})` : <Text type="danger">Shop nicht gefunden</Text>,
            // TODO: Verlinkung zum Shop? Filterung?
        },
        {
            title: 'Anz. Meldungen', // NEUE SPALTE
            dataIndex: 'reportCount',
            key: 'reportCount',
            align: 'center',
            sorter: (a, b) => a.reportCount - b.reportCount,
            render: (count) => (
                <Tag 
                    color={getReportCountTagColor(count)} 
                    icon={count >= 2 ? <WarningFilled /> : null}
                >
                    {count}x
                </Tag>
            )
        },
        {
            title: 'Grund (neueste)',
            dataIndex: 'reason',
            key: 'reason',
            render: (reason) => reason || <Text type="secondary">Kein Grund angegeben</Text>,
            ellipsis: true, // Kürzt langen Text
        },
        {
            title: 'Melder (neueste)',
            key: 'reportedBy',
            render: (_, record) => record.reportedBy ? record.reportedBy.name || record.reportedBy.email : <Text type="secondary">Unbekannt</Text>,
        },
        {
            title: 'Letzte Meldung am',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        },
        {
            title: 'Admin Notiz',
            key: 'notes',
            render: (_, record) => (
                <TextArea
                    rows={2}
                    value={resolutionNotes[record._id] || ''}
                    onChange={(e) => handleNoteChange(record._id, e.target.value)}
                    placeholder="Optional: Grund für Aktion..."
                    disabled={actionLoading[`inaccuracy-${record._id}`]}
                />
            ),
        },
        {
            title: 'Aktionen',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                     <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        // Verwende die _id der neuesten Meldung (die Haupt-ID des aggregierten Eintrags)
                        onClick={() => handleInaccuracyAction(record._id, 'resolve')}
                        loading={actionLoading[`inaccuracy-${record._id}`]}
                        disabled={actionLoading[`inaccuracy-${record._id}`]}
                        style={{ width: '100%' }}
                        title="Bestätigt die Meldung(en) und deaktiviert den zugehörigen Basic-Shop."
                    >
                        Bestätigen & Deaktivieren
                    </Button>
                    <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        // Verwende die _id der neuesten Meldung
                        onClick={() => handleInaccuracyAction(record._id, 'reject')}
                        loading={actionLoading[`inaccuracy-${record._id}`]}
                        disabled={actionLoading[`inaccuracy-${record._id}`]}
                        style={{ width: '100%' }}
                        title="Lehnt die Meldung(en) ab, der Shop bleibt unverändert."
                    >
                        Ablehnen
                    </Button>
                </Space>
            ),
        },
    ];

    // NEU: Spalten für ausstehende Bewertungen
    const pendingReviewColumns = [
        { 
            title: 'Shop', 
            key: 'shop', 
            // Nutze populated Shop-Daten, falls vorhanden
            render: (_, record) => record.shop?.name || <Text type="secondary">Unbekannt</Text>,
            sorter: (a, b) => (a.shop?.name || '').localeCompare(b.shop?.name || '')
        },
        { 
            title: 'Autor', 
            dataIndex: 'author', 
            key: 'author',
            sorter: (a, b) => a.author.localeCompare(b.author) 
        },
        { 
            title: 'Bewertung', 
            dataIndex: 'rating', 
            key: 'rating',
            align: 'center',
            render: (rating) => <Rate disabled defaultValue={rating} style={{ fontSize: 16 }} />,
            sorter: (a, b) => a.rating - b.rating 
        },
        { 
            title: 'Text', 
            dataIndex: 'text', 
            key: 'text', 
            ellipsis: true, // Kürzen, falls zu lang
            render: (text) => <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'mehr' }}>{text}</Paragraph>
        },
        { 
            title: 'Erstellt am', 
            dataIndex: 'createdAt', 
            key: 'createdAt', 
            render: (date) => formatDate(date), 
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) 
        },
        {
            title: 'Aktionen',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleApproveReview(record._id)}
                        loading={actionLoading[`review-approve-${record._id}`]}
                        disabled={!!actionLoading[`review-approve-${record._id}`] || !!actionLoading[`review-delete-${record._id}`]} // Deaktivieren bei jeder Aktion für diesen Eintrag
                    >
                        Genehmigen
                    </Button>
                    <Popconfirm
                        title="Sicher, dass Sie diese Bewertung löschen möchten?"
                        onConfirm={() => handleDeleteReview(record._id)}
                        okText="Ja, löschen"
                        cancelText="Abbrechen"
                        okButtonProps={{ loading: actionLoading[`review-delete-${record._id}`], danger: true }}
                        disabled={!!actionLoading[`review-approve-${record._id}`] || !!actionLoading[`review-delete-${record._id}`]} // Deaktivieren bei jeder Aktion für diesen Eintrag
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={actionLoading[`review-delete-${record._id}`]}
                            disabled={!!actionLoading[`review-approve-${record._id}`] || !!actionLoading[`review-delete-${record._id}`]} // Deaktivieren bei jeder Aktion für diesen Eintrag
                        >
                            Löschen
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // --- Render ---

    return (
        <>
             <Helmet>
                 <title>Admin Verwaltung | Snus Finder</title>
                 <meta name="description" content="Verwaltung gemeldeter Shops und ausstehender Bewertungen." />
                 <meta name="robots" content="noindex, nofollow" />
             </Helmet>
            <div className="admin-management-page" style={{ padding: '20px' }}>
                <Title level={2} style={{ marginBottom: '20px' }}>Admin Verwaltung</Title>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab={`Ausstehende Bewertungen (${pendingReviews.length})`} key="pendingReviews">
                        <Paragraph>
                            Hier sind alle von Gästen oder noch nicht genehmigten Nutzern geschriebenen Bewertungen, die auf Freischaltung warten.
                        </Paragraph>
                        {loadingPendingReviews ? (
                             <Spin tip="Lade ausstehende Bewertungen..."><div style={{ height: '200px' }}></div></Spin>
                        ) : (
                            <Table
                                columns={pendingReviewColumns}
                                dataSource={pendingReviews}
                                rowKey="_id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                            />
                        )}
                    </TabPane>
                    <TabPane tab={`Gemeldete Shops (${reportedShops.length})`} key="reported">
                        <Paragraph>
                            Hier siehst du alle von Nutzern neu gemeldeten Shops, die noch auf Genehmigung oder Ablehnung warten.
                        </Paragraph>
                        {loadingReported ? (
                            <Spin tip="Lade gemeldete Shops..."><div style={{ height: '200px' }}></div></Spin>
                        ) : (
                            <Table
                                columns={reportedColumns}
                                dataSource={reportedShops}
                                rowKey="_id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                            />
                        )}
                    </TabPane>
                    <TabPane tab={`Gemeldete Ungenauigkeiten (${inaccuracyReports.length})`} key="inaccuracy">
                        <Paragraph>
                            Hier sind Meldungen von Nutzern über bestehende Basic-Shops, die fehlerhafte Daten haben könnten (z.B. keinen Snus mehr verkaufen). Shops mit mehr offenen Meldungen erscheinen zuerst.
                        </Paragraph>
                        {loadingInaccuracy ? (
                             <Spin tip="Lade Ungenauigkeitsmeldungen..."><div style={{ height: '200px' }}></div></Spin>
                        ) : (
                            <Table
                                columns={inaccuracyColumns}
                                dataSource={inaccuracyReports}
                                rowKey="_id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                            />
                        )}
                    </TabPane>
                </Tabs>
            </div>
        </>
    );
};

export default AdminManagementPage; 